import { useState } from "react";
import { useForm } from "react-hook-form";
import { Can } from "@core/permissions";
import { ApiError } from "@core/http";
import { markErrorHandled, reportError } from "@portal/common/errorMessage";
import { Icon } from "@core/icons";
import { tokens } from "@core/theme/tokens";
import { ImageWithPlaceholder } from "@core/media";
import {
  AlertText,
  DialogHeader,
  EmptyState,
  Field,
  FeedbackState,
  FileUploadChip,
  FormLayout,
  ImagePreviewBox,
  Modal,
  PortalButton,
  inputStyle,
  RowIconAction,
  selectStyle,
  useToast,
} from "@portal/common";
import {
  catalogLabel,
  resolveCatalogId,
  type CatalogEntry,
  type Vehicle,
  type VehicleInput,
} from "./api";
import { vehicleFormSchema, type VehicleFormValues } from "./vehicleSchemas";
import {
  useVehicleCatalog,
  useVehicleMutations,
  useVehicles,
} from "./vehicleQueries";
import { MaintenancePanel } from "./MaintenancePanel";
import { formatShortDate } from "@core/format/shortDate";

// Medida literal de la miniatura de fila (admin.jsx:939-940): 62×42, radio 9,
// borde de una línea. DESIGN-TOKENS.md no define una escala de tamaño de
// imagen, así que vive acá igual que catalogStyles.ts para sus propias
// medidas de layout.
const thumb = { width: 62, height: 42, radius: 9 };

// admin.jsx:947-949: punto de 11px con el color del vehículo antes del
// nombre del color.
const colorDot = { size: 11 };

// `Vehicle.color` (modelo de lectura) solo trae la etiqueta, no el id del
// catálogo (contrato, sin cambios posibles acá — ver comentario de
// `resolveCatalogId` en ./api). El hex vive en `CatalogEntry.attributes.color`
// del catálogo `colors` (mismo patrón que `CatalogsScreen.tsx` para
// `referral-actions`): se resuelve cruzando la etiqueta contra el catálogo ya
// cargado, con el mismo *fallback* neutro que usa esa pantalla cuando no hay
// coincidencia o el catálogo no trae el atributo.
const colorHex = (entries: CatalogEntry[], label?: string | null) => {
  if (!label) return undefined;
  const entry = entries.find((candidate) => catalogLabel(candidate) === label);
  const attrs = (entry?.attributes ?? {}) as Record<string, unknown>;
  return typeof attrs.color === "string" ? attrs.color : undefined;
};

// PLAN-009 §3: viaja como `codeMessages` a `reportError` (toast, tono
// error) — un código sin mapear trae el genérico con correlación.
const VEHICLE_ERROR_MESSAGES: Record<string, string> = {
  PLATE_DUPLICATE: "Ya existe un vehículo activo con esa placa",
  REASON_REQUIRED: "Indica el motivo de la baja del vehículo",
};

function VehicleForm({
  initial,
  editing,
  models,
  colors,
  onSubmit,
  onCancel,
  submitting,
  modal,
}: {
  initial?: Vehicle;
  editing?: boolean;
  models: CatalogEntry[];
  colors: CatalogEntry[];
  onSubmit: (value: VehicleInput) => void;
  onCancel: () => void;
  submitting?: boolean;
  // Revisión en vivo 2026-09-22, puntos #3/#4A/#15: este formulario ES el
  // diálogo, igual que `MemberForm`/`UserForm`/`ProfileForm`. Antes la
  // pantalla lo envolvía en un `Modal` y él dibujaba a mano su título y su
  // fila de botones con `.btn`/`.btn-primary` crudos, que se desplazaban
  // junto con el contenido.
  modal: { label: string; width?: number | string; onClose?: () => void };
}) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors },
  } = useForm<VehicleFormValues>({
    defaultValues: {
      modelId: resolveCatalogId(models, initial?.model),
      year: initial?.year ? String(initial.year) : "",
      plate: initial?.plate ?? "",
      // SPEC-202/SPEC-203, bug QA-MTZ-002 #7 (color desconectado al editar):
      // verificado que `VehicleController.toDto` ya devuelve `color`
      // (api-dto, agent-logs/runs/2026-09-18-qa-silent-data-loss/
      // 02-api-dto-attempt-1.md) — `resolveCatalogId` lo encuentra sin
      // ningún cambio acá. Cubierto por
      // `VehiclesPanel.test.tsx#"conserva el color al editar un vehículo sin
      // tocar el selector"`.
      colorId: resolveCatalogId(colors, initial?.color),
      sapCode: initial?.sapCode ?? "",
      acquiredOn: initial?.acquiredOn ?? "",
      photoUrl: initial?.photoUrl ?? "",
      photoFileId: undefined,
    },
  });
  const photoUrl = watch("photoUrl");
  return (
    <FormLayout
      // Punto #21 (revisión en vivo 2026-09-22): sin un campo nombre, el
      // eyebrow es la sección y el título sigue siendo la acción.
      eyebrow="Vehículos"
      title={editing ? "Editar vehículo" : "Agregar vehículo"}
      modal={modal}
      actions={
        <>
          <PortalButton type="button" tone="secondary" onClick={onCancel}>
            Cancelar
          </PortalButton>
          <PortalButton type="submit" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar vehículo"}
          </PortalButton>
        </>
      }
      onSubmit={handleSubmit((value) => {
        const parsed = vehicleFormSchema.safeParse(value);
        if (!parsed.success) {
          for (const issue of parsed.error.issues)
            setError(issue.path[0] as keyof VehicleFormValues, {
              message: issue.message,
            });
          return;
        }
        const data = parsed.data;
        onSubmit({
          modelId: data.modelId,
          year: Number(data.year),
          plate: data.plate.toUpperCase(),
          // `colorId` es `nullable: true` en el contrato (20-members-points.yaml):
          // a diferencia de los campos opcionales de abajo, acá se manda
          // explícitamente `null` cuando no hay selección en vez de omitir la
          // clave, para que "sin color" sea una instrucción de borrado
          // inequívoca en el `PUT` de edición, no una ausencia ambigua.
          colorId: data.colorId ? data.colorId : null,
          ...(data.sapCode ? { sapCode: data.sapCode } : {}),
          ...(data.acquiredOn ? { acquiredOn: data.acquiredOn } : {}),
          ...(data.photoUrl ? { photoUrl: data.photoUrl } : {}),
          // PLAN-007 Fase 4d: el `fileId` viaja solo cuando el operador subió
          // un archivo nuevo en esta sesión del formulario; el servidor le
          // da prioridad sobre `photoUrl` (VehicleInput.photoFileId). Si no
          // se tocó la imagen, `photoFileId` queda `undefined` y solo viaja
          // la `photoUrl` heredada — nunca se pierde.
          ...(data.photoFileId ? { photoFileId: data.photoFileId } : {}),
        });
      })}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Modelo" required error={errors.modelId?.message}>
          <select
            aria-label="Modelo"
            style={inputStyle}
            {...register("modelId")}
          >
            <option value="">Selecciona un modelo</option>
            {models.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {catalogLabel(entry)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Año" required error={errors.year?.message}>
          <input
            aria-label="Año"
            style={inputStyle}
            placeholder="2026"
            {...register("year")}
          />
        </Field>
        <Field label="Placa" required error={errors.plate?.message}>
          <input
            aria-label="Placa"
            style={inputStyle}
            placeholder="PCA-1234"
            {...register("plate")}
          />
        </Field>
        <Field label="Color">
          <select
            aria-label="Color"
            style={inputStyle}
            {...register("colorId")}
          >
            <option value="">Sin especificar</option>
            {colors.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {catalogLabel(entry)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Código SAP">
          <input
            aria-label="Código SAP"
            style={inputStyle}
            placeholder="SAP-000000"
            {...register("sapCode")}
          />
        </Field>
        <Field label="Fecha de adquisición">
          <input
            aria-label="Fecha de adquisición"
            type="date"
            style={inputStyle}
            {...register("acquiredOn")}
          />
        </Field>
        <Field label="Fotografía" error={errors.photoUrl?.message}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ImagePreviewBox imageUrl={photoUrl ?? ""} />
            <FileUploadChip
              entityType="vehicle"
              label="Seleccionar imagen"
              ariaLabel="Subir fotografía del vehículo"
              onUploaded={(image) => {
                // PLAN-007 Fase 4d: `imageUrl` solo para la previsualización
                // inmediata en pantalla (vigente 15 minutos); `fileId` es lo
                // que de verdad viaja al servidor (ver `onSubmit` arriba).
                setValue("photoUrl", image.imageUrl, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue("photoFileId", image.fileId, { shouldDirty: true });
              }}
            />
          </div>
        </Field>
      </div>
    </FormLayout>
  );
}

// SPEC-203 §3.1 / ANNEX-012 §2.2: las cifras de impacto (saldo, avance, meta)
// solo son autoritativas cuando vienen del servidor en el `409 LAST_VEHICLE`
// de ese instante — no del `Member` que bajó con la ficha en otro momento, y
// no de un conteo local de filas cacheadas por TanStack Query.
interface DeactivationImpact {
  balanceAtRisk: number;
  // El backend devuelve `null` legítimamente cuando el miembro no tiene
  // conteo del Gran Premio abierto (sin backfill histórico, ver hallazgo de
  // spec-verifier). `null` significa "no lo sabemos", nunca "cero": aplastarlo
  // a un número fabricaría una cifra en una advertencia de pérdida irreversible.
  progressAtRisk: number | null;
  goal: number | null;
}

const asNumber = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const asNullableNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

export function VehiclesPanel({ memberId }: { memberId: string }) {
  const vehicles = useVehicles(memberId);
  const models = useVehicleCatalog("car-models");
  const colors = useVehicleCatalog("colors");
  const reasons = useVehicleCatalog("inactivation-reasons");
  const mutations = useVehicleMutations(memberId);
  const toast = useToast();
  const [editing, setEditing] = useState<Vehicle | null | undefined>(undefined);
  const [deactivating, setDeactivating] = useState<Vehicle | null>(null);
  const [reason, setReason] = useState("");
  const [impact, setImpact] = useState<DeactivationImpact | null>(null);
  const [maintenanceVehicle, setMaintenanceVehicle] = useState<Vehicle | null>(
    null,
  );

  const rows = vehicles.data ?? [];
  const activeCount = rows.filter((v) => v.active).length;

  // Revisión en vivo 2026-09-22: el disparador "⋯" con `role="menu"` se
  // reemplaza por acciones `RowIconAction` en la fila, igual que las demás
  // tablas del portal (Usuarios, Miembros). Mismos handlers, mismas
  // condiciones de permiso/visibilidad que tenía cada ítem del menú.
  // QA-MTZ-002 #5 (decisión humana 2026-09-23): se quitó la acción «Ver»;
  // solo lanzaba un toast `modelo · placa` (copia del botón de relleno del
  // prototipo, admin.jsx:1030) y SPEC-203 no define un detalle de vehículo.

  const save = (input: VehicleInput) => {
    if (editing)
      mutations.edit.mutate(
        { id: editing.id, input },
        {
          onSuccess: () => {
            setEditing(undefined);
            toast.show("Vehículo actualizado correctamente.");
          },
          onError: (e) => reportError(toast, e, VEHICLE_ERROR_MESSAGES),
        },
      );
    else
      mutations.create.mutate(input, {
        onSuccess: () => {
          setEditing(undefined);
          toast.show("Vehículo agregado a la flota.");
        },
        onError: (e) => reportError(toast, e, VEHICLE_ERROR_MESSAGES),
      });
  };

  // SPEC-203 §3.1 / contrato `portalDeactivateVehicle`: la primera llamada va
  // siempre sin `confirmed` (o `false`). Si el servidor responde `204`, no era
  // el último vehículo activo y no había nada que advertir. Si responde `409
  // LAST_VEHICLE`, recién ahí se muestra el impacto que trae la respuesta —
  // nunca el que ya teníamos en props — y solo una segunda confirmación
  // explícita repite la llamada con `confirmed: true`.
  const confirmDeactivate = () => {
    if (!deactivating || !reason) return;
    mutations.deactivate.mutate(
      {
        id: deactivating.id,
        input: { deactivationReasonId: reason, confirmed: Boolean(impact) },
      },
      {
        onSuccess: () => {
          setDeactivating(null);
          setImpact(null);
          toast.show("Vehículo dado de baja.");
        },
        onError: (e) => {
          if (e instanceof ApiError && e.code === "LAST_VEHICLE") {
            const raw = e.problem?.impact ?? {};
            setImpact({
              balanceAtRisk: asNumber(raw.balanceAtRisk),
              progressAtRisk: asNullableNumber(raw.progressAtRisk),
              goal: asNullableNumber(raw.goal),
            });
            markErrorHandled(e);
            return;
          }
          reportError(toast, e, VEHICLE_ERROR_MESSAGES);
        },
      },
    );
  };

  return (
    <div data-testid="vehicles-panel-content">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "18px 20px 14px",
        }}
      >
        <div>
          <span className="label">Flota asociada</span>
          <div
            style={{
              font: `400 12px ${tokens.font.ui}`,
              color: tokens.text.tertiary,
              marginTop: 5,
            }}
          >
            {rows.length
              ? `${rows.length} vehículos · ${activeCount} activos`
              : "Sin vehículos registrados"}
          </div>
        </div>
        <Can module="members" action="canCreate">
          <PortalButton size="small" onClick={() => setEditing(null)}>
            <Icon name="plus" size={15} />
            Agregar vehículo
          </PortalButton>
        </Can>
      </div>
      {vehicles.isLoading ? (
        <FeedbackState kind="loading" />
      ) : vehicles.isError ? (
        <EmptyState kind="error" />
      ) : rows.length ? (
        <div style={{ borderTop: `1px solid ${tokens.surface.line}` }}>
          {rows.map((v) => (
            <div
              key={v.id}
              style={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(150px,1.4fr) 82px minmax(100px,1fr) minmax(86px,0.8fr) 84px 220px",
                gap: 10,
                alignItems: "center",
                padding: "12px 20px",
                borderBottom: `1px solid ${tokens.surface.line}`,
                position: "relative",
                opacity: v.active ? 1 : 0.55,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 11,
                  minWidth: 0,
                }}
              >
                <ImageWithPlaceholder
                  src={v.photoUrl}
                  icon="car"
                  iconSize={18}
                  showText={false}
                  imageStyle={{
                    width: thumb.width,
                    height: thumb.height,
                    objectFit: "cover",
                    borderRadius: thumb.radius,
                    border: `1px solid ${tokens.surface.line}`,
                    background: tokens.surface.surface2,
                    flex: "none",
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      font: `600 12.5px ${tokens.font.ui}`,
                      color: tokens.text.primary,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {v.model}
                  </div>
                  <div
                    style={{
                      font: `400 10.5px ${tokens.font.ui}`,
                      color: tokens.text.disabled,
                      marginTop: 2,
                    }}
                  >
                    {v.year}
                    {v.version ? ` · ${v.version}` : ""}
                  </div>
                </div>
              </div>
              <span
                style={{
                  font: `600 12.5px ${tokens.font.ui}`,
                  color: tokens.text.primary,
                  letterSpacing: "0.03em",
                }}
              >
                {v.plate}
              </span>
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  font: `400 12.5px ${tokens.font.ui}`,
                  color: tokens.text.secondary,
                }}
              >
                <span
                  data-testid="vehicle-color-dot"
                  style={{
                    width: colorDot.size,
                    height: colorDot.size,
                    borderRadius: "50%",
                    background:
                      colorHex(colors.data ?? [], v.color) ??
                      tokens.text.tertiary,
                    border: `1px solid ${tokens.surface.line2}`,
                    flex: "none",
                  }}
                />
                {v.color ?? "—"}
              </span>
              <span
                style={{
                  font: `400 12.5px ${tokens.font.ui}`,
                  color: tokens.text.secondary,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {v.sapCode ?? "—"}
              </span>
              <span
                style={{
                  font: `400 12.5px ${tokens.font.ui}`,
                  color: tokens.text.secondary,
                }}
              >
                {formatShortDate(v.acquiredOn)}
              </span>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <RowIconAction
                  label="Mantenimientos"
                  icon="wrench"
                  onClick={() => setMaintenanceVehicle(v)}
                />
                <Can module="members" action="canEdit">
                  <RowIconAction
                    label="Editar"
                    icon="edit"
                    onClick={() => setEditing(v)}
                  />
                </Can>
                {v.active ? (
                  <Can module="members" action="canInactivate">
                    <RowIconAction
                      label="Dar de baja"
                      icon="deactivate"
                      tone="danger"
                      onClick={() => {
                        setDeactivating(v);
                        setReason("");
                        setImpact(null);
                      }}
                    />
                  </Can>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // admin.jsx:971: copy propio de este panel, distinto del genérico de
        // `EmptyState kind="no-data"` — un párrafo centrado bajo el borde
        // superior, sin ícono ni acción, dentro del alcance de este archivo.
        <div
          style={{
            padding: "30px 20px 34px",
            textAlign: "center",
            font: `400 12.5px ${tokens.font.ui}`,
            color: tokens.text.tertiary,
            borderTop: `1px solid ${tokens.surface.line}`,
          }}
        >
          Aún no hay vehículos asociados a este miembro.
        </div>
      )}
      {editing !== undefined &&
      // Al editar, `resolveCatalogId` necesita el catálogo real ya cargado
      // para preseleccionar modelo/color desde `initial`; `useForm` solo lee
      // `defaultValues` en el primer render, así que abrir el modal con el
      // catálogo todavía vacío dejaría esos campos sin resolver de forma
      // permanente para ese montaje. Al agregar (sin `initial`) no aplica.
      (!editing || (models.data && colors.data)) ? (
        <VehicleForm
          editing={Boolean(editing)}
          {...(editing ? { initial: editing } : {})}
          models={models.data ?? []}
          colors={colors.data ?? []}
          onSubmit={save}
          onCancel={() => setEditing(undefined)}
          submitting={mutations.create.isPending || mutations.edit.isPending}
          modal={{
            label: editing ? "Editar vehículo" : "Agregar vehículo",
            width: 640,
            onClose: () => setEditing(undefined),
          }}
        />
      ) : null}
      {deactivating ? (
        <Modal
          role="alertdialog"
          label="Confirmar baja de vehículo"
          width={500}
          onClose={() => {
            setDeactivating(null);
            setImpact(null);
          }}
          // Revisión en vivo 2026-09-22, puntos #15 y #18: gemelo exacto de
          // "¿Inactivar miembro?" (`<h2>`/`<p>`/`<label>` crudos, botones
          // `.btn`/`.btn-primary` y un `<select>` sin ningún estilo). Se
          // converge a la misma forma de tres regiones.
          // Punto #21 (misma revisión): encabezado común de diálogos.
          header={
            <DialogHeader eyebrow="Vehículos" title="¿Inactivar vehículo?" />
          }
          footer={
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <PortalButton
                type="button"
                tone="secondary"
                size="small"
                onClick={() => {
                  setDeactivating(null);
                  setImpact(null);
                }}
              >
                Cancelar
              </PortalButton>
              <PortalButton
                type="button"
                tone="danger"
                size="small"
                disabled={!reason || mutations.deactivate.isPending}
                onClick={confirmDeactivate}
              >
                {impact ? "Confirmar de todas formas" : "Sí, inactivar"}
              </PortalButton>
            </div>
          }
        >
          <p style={{ margin: "0 0 14px" }}>
            {deactivating.model} · {deactivating.plate} queda fuera del programa
            de mantenimientos. El historial registrado se conserva.
          </p>
          {impact ? (
            <AlertText style={{ margin: "0 0 14px" }}>
              Este es el último vehículo activo del miembro: al confirmarlo,
              quedará sin vehículo Mercedes-Benz activo y perderá{" "}
              {impact.balanceAtRisk} puntos de saldo.{" "}
              {impact.progressAtRisk === null || impact.goal === null
                ? "No se puede determinar su avance hacia el Gran Premio para este miembro."
                : `Su avance de ${impact.progressAtRisk} de ${impact.goal} hacia el Gran Premio también se perderá.`}
            </AlertText>
          ) : null}
          <Field label="Motivo de la baja">
            <select
              aria-label="Motivo de la baja"
              value={reason}
              style={selectStyle}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="">Selecciona un motivo</option>
              {(reasons.data ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {catalogLabel(entry)}
                </option>
              ))}
            </select>
          </Field>
        </Modal>
      ) : null}
      {maintenanceVehicle ? (
        <MaintenancePanel
          vehicle={maintenanceVehicle}
          onClose={() => setMaintenanceVehicle(null)}
        />
      ) : null}
    </div>
  );
}
